/**
 * touchControls.ts — The on-screen control: a floating virtual stick.
 *
 * Purely presentational plumbing. It draws the stick from the state `Input`
 * reports — it never touches gameplay. Input owns the gesture; this owns the
 * pixels.
 *
 * The stick is drawn only while a drag is in progress, so it never sits on
 * screen getting in the way of the game.
 *
 * ## There used to be a button here
 *
 * A green "Hold to clean" button in the bottom-right corner, shown while the
 * dog's mess was on the pavement and the walker stood in range of it, because
 * the cleanup was a three-second hold on Space and a keyboard-only action is a
 * dead end on a phone. That whole step is gone — see `types.ts` →
 * {@link Objective} — and with it the last thing in the game that was pressed
 * rather than steered. **The walk is now entirely a steering game**, which is
 * why this file is one control and not three: nothing else needs an on-screen
 * stand-in, and the corner of a phone screen is back to being scenery.
 */
import { TOUCH } from '../config/constants';
import type { Input, JoystickView } from '../core/Input';

const CSS = `
.tc{position:fixed;inset:0;pointer-events:none;z-index:11;
  font-family:system-ui,Arial,sans-serif}
.tc-stick{position:absolute;border-radius:50%;display:none;
  border:2px solid rgba(255,255,255,.45);background:rgba(255,255,255,.10);
  box-shadow:0 2px 14px rgba(0,0,0,.35)}
.tc-knob{position:absolute;border-radius:50%;
  background:rgba(255,255,255,.75);box-shadow:0 2px 10px rgba(0,0,0,.4)}
`;

export class TouchControls {
  private root: HTMLDivElement;
  private stick: HTMLDivElement;
  private knob: HTMLDivElement;
  private detach: Array<() => void> = [];

  constructor(
    parent: HTMLElement,
    private input: Input,
  ) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    this.detach.push(() => style.remove());

    this.root = el('div', 'tc');

    // --- floating stick ---
    this.stick = el('div', 'tc-stick');
    this.stick.style.width = `${TOUCH.BASE_SIZE}px`;
    this.stick.style.height = `${TOUCH.BASE_SIZE}px`;
    this.knob = el('div', 'tc-knob');
    this.knob.style.width = `${TOUCH.KNOB_SIZE}px`;
    this.knob.style.height = `${TOUCH.KNOB_SIZE}px`;
    this.stick.appendChild(this.knob);
    this.root.appendChild(this.stick);

    parent.appendChild(this.root);

    this.wireJoystick();
  }

  /** Redraw the stick whenever Input says it moved. */
  private wireJoystick(): void {
    this.input.onJoystickChange = (v: JoystickView): void => {
      if (!v.active) {
        this.stick.style.display = 'none';
        return;
      }
      this.stick.style.display = 'block';
      this.stick.style.left = `${v.originX - TOUCH.BASE_SIZE / 2}px`;
      this.stick.style.top = `${v.originY - TOUCH.BASE_SIZE / 2}px`;
      // Knob is centred in the ring, then offset by the (clamped) drag.
      const c = (TOUCH.BASE_SIZE - TOUCH.KNOB_SIZE) / 2;
      this.knob.style.left = `${c + v.knobX}px`;
      this.knob.style.top = `${c + v.knobY}px`;
    };
    this.detach.push(() => {
      this.input.onJoystickChange = undefined;
    });
  }

  dispose(): void {
    for (const fn of this.detach) fn();
    this.detach = [];
    this.root.remove();
  }
}

function el<T extends HTMLElement = HTMLDivElement>(tag: string, className: string): T {
  const node = document.createElement(tag) as T;
  node.className = className;
  return node;
}
